import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../../utils/api';
import { prepareFileForUpload } from '../../../utils/imageUploadCompress';
import { useAuth } from '../../../context/AuthContext';
import { canManageCrmTickets, canAssignCrmTickets } from '../../../utils/crmPermissions';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import DateInput from '../../ui/date-input';
import { useToast } from '../../../hooks/use-toast';
import { CRM_TICKET_CATEGORIES, CRM_TICKET_PRIORITIES, ticketCategoryLabel } from '../../../data/crmTicketConstants';
import CrmTicketStatusBadge from './CrmTicketStatusBadge';
import CrmTicketPriorityBadge from './CrmTicketPriorityBadge';
import CrmTicketSlaBadge from './CrmTicketSlaBadge';
import CrmTicketComments from './CrmTicketComments';
import CrmTicketTimeline from './CrmTicketTimeline';
import { ArrowLeft } from 'lucide-react';
import CrmTicketAttachmentPreview from './CrmTicketAttachmentPreview';
import AsyncSubmitButton from '../../ui/AsyncSubmitButton';
import { fetchTicketAttachmentPreviewBlob } from '../../../utils/crmTicketDownload';

function employeeLabel(emp) {
  const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
  return name ? `${name} (${emp.employee_code})` : emp.employee_code;
}

function TicketAttachmentThumb({ attachment, onOpen }) {
  const [src, setSrc] = useState(null);
  const name = attachment?.original_name || 'Image';

  useEffect(() => {
    if (!attachment?.id) return undefined;
    let objectUrl = null;
    let cancelled = false;
    fetchTicketAttachmentPreviewBlob(attachment.id)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment?.id]);

  return (
    <button
      type="button"
      onClick={onOpen}
      className="block w-full text-left rounded-lg overflow-hidden border border-border hover:border-primary/50 transition-colors"
    >
      {src ? (
        <img src={src} alt={name} className="w-full h-auto object-cover max-h-40 bg-muted" />
      ) : (
        <div className="h-28 w-full animate-pulse bg-muted" />
      )}
      <span className="block px-2 py-1.5 text-xs text-muted-foreground truncate">{name}</span>
    </button>
  );
}

export default function CrmTicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const isManager = canManageCrmTickets(user);
  const canAssign = canAssignCrmTickets(user);
  const [data, setData] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [schools, setSchools] = useState([]);
  const [form, setForm] = useState({});
  const [previewAtt, setPreviewAtt] = useState(null);

  const load = () => {
    api.get(`/crm/tickets/${id}`).then((res) => {
      const d = res.data?.data;
      setData(d);
      setForm({
        title: d?.title || '',
        description: d?.description || '',
        category: d?.category || 'other',
        priority: d?.priority || 'medium',
        status: d?.status || 'open',
        assigned_employee_id: d?.assigned_employee_id ? String(d.assigned_employee_id) : 'unassigned',
        school_id: d?.school_id ? String(d.school_id) : 'none',
        due_date: d?.due_date ? d.due_date.slice(0, 10) : '',
        estimated_minutes: d?.estimated_minutes ?? '',
        spent_minutes: d?.spent_minutes ?? 0,
      });
    }).catch(() => {
      toast({ title: 'Error', description: 'Ticket not found', variant: 'destructive' });
      setData(null);
    });
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    api.get('/crm/tickets/meta').then((r) => setStatuses(r.data?.data?.statuses || [])).catch(() => {});
    if (isManager || canAssign) {
      api.get('/crm/employees').then((r) => setEmployees((r.data?.data?.items || []).filter((e) => e.status === 'active'))).catch(() => {});
      api.get('/schools', { params: { limit: 200 } }).then((r) => setSchools(r.data?.data?.items || r.data?.data || [])).catch(() => {});
    }
  }, [isManager, canAssign]);

  const save = async ({ beginLoading }) => {
    beginLoading();
    try {
      const payload = isManager ? {
        title: form.title,
        description: form.description,
        category: form.category,
        priority: form.priority,
        status: form.status,
        school_id: form.school_id === 'none' ? null : Number(form.school_id),
        due_date: form.due_date || null,
        estimated_minutes: form.estimated_minutes !== '' ? Number(form.estimated_minutes) : null,
        spent_minutes: Number(form.spent_minutes) || 0,
      } : {
        status: form.status,
        spent_minutes: Number(form.spent_minutes) || 0,
      };
      if (canAssign) {
        payload.assigned_employee_id = form.assigned_employee_id === 'unassigned' ? null : Number(form.assigned_employee_id);
      }
      await api.put(`/crm/tickets/${id}`, payload);
      toast({ title: 'Saved' });
    } catch (e) {
      toast({
        title: 'Error',
        description: e.response?.data?.message || e.message || 'Save failed',
        variant: 'destructive',
      });
      throw e;
    }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const prepared = await prepareFileForUpload(file);
      const fd = new FormData();
      fd.append('file', prepared);
      await api.post(`/crm/tickets/${id}/attachments`, fd);
      toast({ title: 'File uploaded' });
      load();
    } catch (err) {
      toast({ title: 'Upload failed', description: err.response?.data?.message || err.message, variant: 'destructive' });
    }
  };

  const selfEmployee = employees.find((e) => Number(e.user_id) === Number(user?.id));
  const otherEmployees = selfEmployee
    ? employees.filter((e) => Number(e.id) !== Number(selfEmployee.id))
    : employees;

  if (!data) {
    return <p className="text-muted-foreground">Loading…</p>;
  }

  const allowedStatuses = data.allowed_statuses || [data.status];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/crm/tickets')}><ArrowLeft className="h-4 w-4" /></Button>
        <div>
          <p className="text-sm text-muted-foreground font-mono">{data.ticket_code}</p>
          <h1 className="text-xl font-semibold">{data.title}</h1>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <CrmTicketStatusBadge status={data.status} statuses={statuses} />
        <CrmTicketPriorityBadge priority={data.priority} />
        <CrmTicketSlaBadge slaStatus={data.sla_status} remainingSeconds={data.remaining_seconds} dueDate={data.due_date} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-border">
            <CardHeader><CardTitle>Description</CardTitle></CardHeader>
            <CardContent>
              {isManager ? (
                <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={6} />
              ) : (
                <p className="text-sm whitespace-pre-wrap">{data.description || '—'}</p>
              )}
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader><CardTitle>Comments</CardTitle></CardHeader>
            <CardContent>
              <CrmTicketComments ticketId={id} comments={data.comments || []} onAdded={load} />
            </CardContent>
          </Card>

          <CrmTicketTimeline items={data.activity || []} />
        </div>

        <div className="space-y-4">
          <Card className="border-border">
            <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div><span className="text-muted-foreground">Created by</span><p>{data.created_by_name}</p></div>
              <div><span className="text-muted-foreground">Created</span><p>{data.created_at}</p></div>
              <div><span className="text-muted-foreground">Category</span><p>{ticketCategoryLabel(data.category)}</p></div>
              {data.resolution_hours != null && (
                <div><span className="text-muted-foreground">Resolution</span><p>{data.resolution_hours}h</p></div>
              )}
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader><CardTitle className="text-base">Edit</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {isManager && (
                <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></div>
              )}
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {allowedStatuses.map((s) => {
                      const st = statuses.find((x) => x.slug === s);
                      return <SelectItem key={s} value={s}>{st?.label || s}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              {isManager && (
                <>
                  <div>
                    <Label>Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CRM_TICKET_PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {canAssign && (
                    <div>
                      <Label>Assigned To</Label>
                      <Select value={form.assigned_employee_id} onValueChange={(v) => setForm((f) => ({ ...f, assigned_employee_id: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {selfEmployee && (
                            <SelectItem value={String(selfEmployee.id)}>Assign to me</SelectItem>
                          )}
                          {otherEmployees.map((e) => <SelectItem key={e.id} value={String(e.id)}>{employeeLabel(e)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label>School</Label>
                    <Select value={form.school_id} onValueChange={(v) => setForm((f) => ({ ...f, school_id: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {schools.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Due Date</Label><DateInput value={form.due_date} onChange={(v) => setForm((f) => ({ ...f, due_date: v }))} /></div>
                  <div><Label>Estimated (minutes)</Label><Input type="number" value={form.estimated_minutes} onChange={(e) => setForm((f) => ({ ...f, estimated_minutes: e.target.value }))} /></div>
                </>
              )}
              <div><Label>Time Spent (minutes)</Label><Input type="number" value={form.spent_minutes} onChange={(e) => setForm((f) => ({ ...f, spent_minutes: e.target.value }))} /></div>
              <AsyncSubmitButton onAction={save} onSuccess={load} loadingLabel="Saving…">
                Save Changes
              </AsyncSubmitButton>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader><CardTitle className="text-base">Attachments</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <Input type="file" onChange={uploadFile} accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.zip" />
              <div className="space-y-2">
                {(data.attachments || []).map((a) => {
                  const isImage = a.mime_type?.startsWith('image/');
                  if (isImage) {
                    return (
                      <TicketAttachmentThumb
                        key={a.id}
                        attachment={a}
                        onOpen={() => setPreviewAtt(a)}
                      />
                    );
                  }
                  return (
                    <button
                      key={a.id}
                      type="button"
                      className="block text-sm text-primary hover:underline text-left"
                      onClick={() => setPreviewAtt(a)}
                    >
                      {a.original_name}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {previewAtt && (
        <CrmTicketAttachmentPreview
          attachment={previewAtt}
          onClose={() => setPreviewAtt(null)}
        />
      )}
    </div>
  );
}
